import { RiStarFill } from "@remixicon/react";
import { cn } from "~/lib/utils";

export interface StarRatingProps {
  rating?: number;
  compact?: boolean;
  color?: string;
  className?: string;
  /** Stroked outline base instead of a flat gray RiStarFill for the unfilled portion. */
  outline?: boolean;
  /** Overrides the star size and gap classes (e.g. responsive sizes for a one-off design). */
  sizeClassName?: string;
  gapClassName?: string;
}

// Path and stroke-width (0.5, in a 16-unit viewBox) taken verbatim from Figma's own
// star asset — RiStarLine's ring thickness is baked into its path geometry and
// isn't adjustable, so a real stroke is used here instead to match Figma exactly.
function StarOutline({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path
        d="M10.1162 5.08789L15.6084 5.52832L11.4238 9.1123L12.7021 14.4717L8 11.5996L3.29785 14.4717L4.57617 9.1123L0.391602 5.52832L5.88379 5.08789L8 0L10.1162 5.08789Z"
        stroke="currentColor"
        strokeWidth="0.75"
      />
    </svg>
  );
}

// Gray base star, plus a colored stroked outline on top when `outline` is set, plus
// a colored star clipped to the fill % on top of both (not RiStarHalfFill, whose
// "half" path renders as a hollow outline instead of transparency).
function StarRating({ rating = 5, compact, color = "text-text-body", className, outline = false, sizeClassName, gapClassName }: StarRatingProps) {
  const clamped = Math.min(5, Math.max(0, rating));
  const sizeClass = sizeClassName ?? (compact ? "h-[17px] w-[17px]" : "h-5 w-5");
  const gapClass = gapClassName ?? (compact ? "gap-1" : "gap-2");

  return (
    <div className={cn("flex", gapClass, className)} role="img" aria-label={`${rating} / 5`}>
      {Array.from({ length: 5 }).map((_, i) => {
        const fillRatio = Math.min(Math.max(clamped - i, 0), 1);
        return (
          <div key={i} className={cn("relative", sizeClass)}>
            <RiStarFill className={cn("text-border", sizeClass)} />
            {outline && <StarOutline className={cn(color, "absolute inset-0", sizeClass)} />}
            {fillRatio > 0 && (
              <div className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: `${fillRatio * 100}%` }}>
                <RiStarFill className={cn(color, sizeClass)} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export { StarRating };
